import { createSuspenseQuery } from "../../utils/queryFactories.js";
import { api } from "../../utils/fetch.js";
import { z } from "zod";
import { ContactSchema } from "../../schemas/contact.js";

const EntityTypeSchema = z.enum(["client", "resource", "task"]);
export type EntityContactType = z.infer<typeof EntityTypeSchema>;

const BaseRequestSchema = z.object({
  id: z.int().positive(),
  api: z.enum(["wf", "public"]),
  entity_type: EntityTypeSchema
});

const RequestSchema = z.discriminatedUnion("api", [
  BaseRequestSchema.extend({
    api: z.literal("wf")
  }),
  BaseRequestSchema.extend({
    api: z.literal("public"),
    slug: z.string()
  })
]);

const RequestVariables = z.union([
  BaseRequestSchema.extend({ api: z.literal("wf") }).omit({ api: true }),
  BaseRequestSchema.extend({ api: z.literal("public"), slug: z.string() }).omit(
    { api: true }
  )
]);

const ResponseSchema = z.array(ContactSchema);

type Response = z.infer<typeof ResponseSchema>;
type Request = z.infer<typeof RequestSchema>;

export type EntityContactsResponse = Response;
export type EntityContactsRequest = Request;

export type EntityContactProps = Request;

const urls = {
  wf: {
    task: "/task/:id/contacts",
    resource: "/resource/:id/contacts",
    client: "/client/:id/contacts"
  },
  public: {
    task: "/public/:slug/task/:id/contacts",
    resource: "/public/:slug/resource/:id/contacts",
    client: "/public/:slug/client/:id/contacts"
  }
} as const;

export const useEntityContactsSuspense = (entity_type: EntityContactType) =>
  createSuspenseQuery<Response, Request, Error>({
    queryKey: [`entity${entity_type}ContactsWithSuspense`],
    staleTime: 1000 * 60,
    fetcher: async (body) => {
      const { api: apiType, ...rest } = body;
      const result = await api<Omit<Request, "api">, Response>({
        method: "GET",
        url: urls[apiType][rest.entity_type],
        requestSchema: RequestVariables,
        responseSchema: ResponseSchema,
        body: rest
      });
      return result;
    }
  });
